import { Component, OnInit, HostListener } from '@angular/core';
import { AuthService } from '../../core/services/auth.service';
import { UserProfile } from '../../core/models/user-profile';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { returnUrlTreeWith, safeInternalReturnUrl } from '../../core/utils/return-url.util';
// Note: firebase import commented out - phone auth moved to PhoneAuthService
// import firebase from 'firebase/compat/app';
// import { environment } from '../environment';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';

@Component({
    selector: 'app-auth',
    templateUrl: './auth.component.html',
    styleUrls: ['./auth.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule, SharedModule]
})
export class AuthComponent implements OnInit {
  // Core properties
  authForm!: FormGroup;
  isLoginMode = true;
  isLoading = false;
  returnUrl!: string;
  
  // UI state
  showPassword = false;
  errorMessage = '';
  
  // Legacy properties (kept for compatibility)
  userProfile?: UserProfile | null = null;
  isMobile: boolean = false;
  credentialCorect = true;
  countries: { [key: string]: string[] } = {};
  cities: string[] = [];
  selectedFile: File | null = null;
  profilePicturePreview: string | ArrayBuffer | null = null;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder, 
    private af: AuthService, 
    private router: Router) {}

  ngOnInit(): void {
    this.checkScreenSize();
    this.returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
    this.initializeForm();
  }

  private initializeForm(): void {
    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      password2: [''],
      fullname: [''],
      rememberMe: [false],
      // Legacy fields
      phoneNumber: [''],
      phonePrefix: [''],
      code: [''],
    });

    // Add conditional validators for registration
    this.authForm.get('password')?.valueChanges.subscribe(() => {
      if (!this.isLoginMode) {
        this.authForm.get('password2')?.updateValueAndValidity();
      }
    });
  }
  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;
  }
  
  async onSubmit(): Promise<void> {
    if (!this.authForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      if (this.isLoginMode) {
        await this.handleLogin();
      } else {
        await this.handleRegistration();
      }
    } catch (error) {
      this.handleAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async handleLogin(): Promise<void> {
    const email = this.authForm.get('email')?.value;
    const password = this.authForm.get('password')?.value;

    // Les erreurs remontent telles quelles à handleAuthError() (l'appelant).
    const result = await this.af.emailPasswordLoginWithLinking(email, password);

    // Check if linking is needed (user has phone account, no password)
    if (result && 'needsLinking' in result && result.needsLinking) {
      const linkState = result.linkState;
      console.log('🔗 [AuthComponent] Email/password linking required:', linkState);

      const existingProviders = linkState.existingProviders || [];

      if (existingProviders.includes('phone')) {
        // Redirect to phone auth with linking mode
        this.router.navigate(['/auth/phone'], {
          queryParams: {
            returnUrl: this.returnUrl,
            linkEmail: 'true',
            email: linkState.pendingEmail
          }
        });
        return;
      }
    }

    // Normal login success
    if (result && 'user' in result && result.user) {
      this.navigateAfterAuth();
    } else {
      this.errorMessage = 'Email ou mot de passe incorrect';
    }
  }

  private async handleRegistration(): Promise<void> {
    const fullname = this.authForm.get('fullname')?.value;
    const email = this.authForm.get('email')?.value;
    const password = this.authForm.get('password')?.value;

    // Add fullname validation for registration
    if (!fullname?.trim()) {
      this.errorMessage = 'Le nom complet est requis';
      return;
    }

    if (!this.passwordsMatch()) {
      this.errorMessage = 'Les mots de passe ne correspondent pas';
      return;
    }

    // Les erreurs remontent telles quelles à handleAuthError() (l'appelant).
    await this.af.signUp(email, password, fullname);

    // ✅ CORRECTION: Si signUp() réussit sans erreur, l'utilisateur est créé et connecté
    // Naviguer immédiatement après le succès
    this.navigateAfterAuth();
  }

  private navigateAfterAuth(): void {
    const returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
    const restore = this.route.snapshot.queryParams['restore'] || null;

    if (restore) {
      // #1160 — cf. `returnUrlTreeWith` : un `returnUrl` porteur de ses propres
      // query params (tous ceux produits par les guards le sont) finissait
      // encodé en un segment unique → 404.
      this.router.navigateByUrl(returnUrlTreeWith(this.router, returnUrl, { restore: true }));
    } else {
      this.router.navigateByUrl(returnUrl);
    }
  }

  private handleAuthError(error: any): void {
    console.error('Auth error:', error);

    if (error?.code) {
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          this.errorMessage = 'Email ou mot de passe incorrect';
          break;
        case 'auth/email-already-in-use':
          this.errorMessage = 'Cette adresse email est déjà utilisée';
          break;
        case 'auth/weak-password':
          this.errorMessage = 'Le mot de passe est trop faible';
          break;
        case 'auth/invalid-email':
          this.errorMessage = 'Adresse email invalide';
          break;
        case 'auth/too-many-requests':
          this.errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
          break;
        case 'auth/wrong-provider':
          // User has account with different provider (Google, Facebook, etc.)
          this.errorMessage = error.message || 'Ce compte utilise une autre méthode de connexion.';
          break;
        default:
          this.errorMessage = 'Une erreur est survenue. Veuillez réessayer';
      }
    } else {
      this.errorMessage = 'Une erreur est survenue. Veuillez réessayer';
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.authForm.controls).forEach(key => {
      const control = this.authForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Google Login - handles account linking flow
   * If user has existing account with different provider (e.g., phone),
   * they will be redirected to authenticate first, then link Google
   */
  async onGoogleLogin() {
    try {
      this.isLoading = true;
      this.errorMessage = '';

      const result = await this.af.googleLogin();

      // Check if linking is needed (user has existing account with different provider)
      if (result && 'needsLinking' in result && result.needsLinking) {
        const linkState = result.linkState;
        console.log('🔗 [AuthComponent] Account linking required:', linkState);

        // Determine which auth method to use based on existing providers
        const existingProviders = linkState.existingProviders || [];

        if (existingProviders.includes('phone')) {
          // Account exists with phone - don't allow linking from login page
          // User must login with phone first, then add Google from their profile
          console.log('📱 [AuthComponent] Account exists with phone - showing message to login via phone');
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PHONE_THEN_ADD_GOOGLE';
        } else if (existingProviders.includes('password')) {
          // Show message to login with email/password first
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PASSWORD';
        } else if (existingProviders.includes('google.com') || existingProviders.includes('google')) {
          // Edge case: Profile exists with Google provider but different UID
          // Check Firestore profile for original auth method
          const firestoreProviders = linkState.existingProfile?.authProviders || [];
          console.log('🔍 [AuthComponent] Firestore profile providers:', firestoreProviders);

          if (firestoreProviders.includes('phone')) {
            // The original account was created via phone
            console.log('📱 [AuthComponent] Original account was phone - showing message');
            this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PHONE_THEN_ADD_GOOGLE';
          } else {
            // Unknown original method
            this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_ORIGINAL_METHOD';
          }
        } else {
          // Unknown provider
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_ORIGINAL_METHOD';
        }

        this.isLoading = false;
        return;
      }

      // Normal login success - redirect
      this.navigateAfterAuth();

    } catch (error: any) {
      console.error('Google login error:', error);
      this.errorMessage = 'AUTH.MESSAGES.ERRORS.GOOGLE_LOGIN_ERROR';
      this.isLoading = false;
    }
  }

  /**
   * Facebook Login - handles account linking flow
   * Same logic as Google login
   */
  async onFacebookLogin() {
    try {
      this.isLoading = true;
      this.errorMessage = '';

      const result = await this.af.facebookLogin();

      // Check if linking is needed
      if (result && 'needsLinking' in result && result.needsLinking) {
        const linkState = result.linkState;
        console.log('🔗 [AuthComponent] Account linking required:', linkState);

        const existingProviders = linkState.existingProviders || [];

        if (existingProviders.includes('phone')) {
          // Account exists with phone - don't allow linking from login page
          // User must login with phone first, then add Facebook from their profile
          console.log('📱 [AuthComponent] Account exists with phone - showing message to login via phone');
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PHONE_THEN_ADD_FACEBOOK';
        } else if (existingProviders.includes('password')) {
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PASSWORD';
        } else if (existingProviders.includes('facebook.com') || existingProviders.includes('facebook')) {
          // Edge case: Profile exists with Facebook provider but different UID
          const firestoreProviders = linkState.existingProfile?.authProviders || [];
          console.log('🔍 [AuthComponent] Firestore profile providers:', firestoreProviders);

          if (firestoreProviders.includes('phone')) {
            // The original account was created via phone
            console.log('📱 [AuthComponent] Original account was phone - showing message');
            this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_PHONE_THEN_ADD_FACEBOOK';
          } else {
            // Unknown original method
            this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_ORIGINAL_METHOD';
          }
        } else {
          this.errorMessage = 'AUTH.MESSAGES.ERRORS.ACCOUNT_EXISTS_USE_ORIGINAL_METHOD';
        }

        this.isLoading = false;
        return;
      }

      // Normal login success
      this.navigateAfterAuth();

    } catch (error: any) {
      console.error('Facebook login error:', error);
      this.errorMessage = 'AUTH.MESSAGES.ERRORS.FACEBOOK_LOGIN_ERROR';
      this.isLoading = false;
    }
  }

  // Modern UI Methods
  switchMode(): void {
    this.isLoginMode = !this.isLoginMode;
    this.errorMessage = '';
    this.initializeForm();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  passwordsMatch(): boolean {
    const password = this.authForm.get('password')?.value;
    const password2 = this.authForm.get('password2')?.value;
    return password === password2;
  }

  getPasswordStrength(): { class: string, text: string } {
    const password = this.authForm.get('password')?.value || '';
    
    if (password.length < 6) {
      return { class: 'weak', text: 'Faible' };
    }
    
    let score = 0;
    
    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Character variety checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    
    if (score < 3) {
      return { class: 'weak', text: 'Faible' };
    } else if (score < 5) {
      return { class: 'medium', text: 'Moyen' };
    } else {
      return { class: 'strong', text: 'Fort' };
    }
  }

  // Phone authentication - Navigate to dedicated phone auth page
  switchToPhoneAuth(): void {
    // Navigate to the new dedicated phone auth component
    const returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
    const restore = this.route.snapshot.queryParams['restore'] || null;

    const queryParams: any = { returnUrl };
    if (restore) {
      queryParams.restore = restore;
    }

    this.router.navigate(['/auth/phone'], { queryParams });
  }

}

